import get from 'lodash/get.js';
import has from 'lodash/has.js';
import isEmpty from 'lodash/isEmpty.js';
import { mapState } from 'vuex';

import moment from '@/plugins/vue-moment.js';
import RecursiveSearch from '@/utilities/RecursiveSearch.js';

export default {
    props: {
        item: {
            required: true,
            type: Object,
        },
    },

    data: () => ({
        search: new RecursiveSearch(),
    }),

    computed: {
        ...mapState('Auth', { authUser: state => state.user }),

        canViewAdministratively() {
            if (!this.authUser) return false;

            return (
                this.authUser.is_admin ||
                this.authUser.isInstructorOf(this.item.course_id) ||
                this.authUser.isGroupInstructorOf(this.item.course_id)
            );
        },

        days() {
            return {
                1: { name: 'Monday' },
                2: { name: 'Tuesday' },
                3: { name: 'Wednesday' },
                4: { name: 'Thursday' },
                5: { name: 'Friday' },
                6: { name: 'Saturday' },
                7: { name: 'Sunday' },
            };
        },

        hasPrerequisite() {
            return !!this.item.prerequisite;
        },

        unlocked() {
            return this.item.unlocked;
        },

        locked() {
            if (this.item.completed) return false;

            if (this.item.unlocked) return false;

            const locked = this.hasPrerequisite && !get(this.target, 'completed');

            const isBreak = get(this.item, 'section_type') === 'break';

            if (isBreak) return true;

            return locked || this.notVisible;
        },

        menu() {
            return this.$store.getters['Menu/menu'];
        },

        notVisible() {
            if (!get(this.item, 'visible_at')) return false;

            if (this.template) {
                return has(this.item.visible_at, 'week');
            }

            const visibleOn = moment.utc(this.item.visible_at).tz(this.$timezone.name);
            const now = moment();

            return visibleOn.diff(now) > 0;
        },

        notVisibleByPermission() {
            const scopedRole = this.authUser.isInstructorOf(this.item.course_id)
                ? 1
                : this.authUser.isGroupInstructorOf(this.item.course_id)
                  ? 2
                  : this.authUser.is_admin
                    ? 3
                    : 0;

            const activitySettingRole = get(this.item, 'course_authorized_role_id');
            const isNotVisible = activitySettingRole > scopedRole;

            if (this.authUser.isStudentOf(this.item.course_id) && activitySettingRole >= 1) {
                return true;
            }

            return isNotVisible;
        },

        target() {
            if (!this.hasPrerequisite) return null;

            return this.find('id', this.item.prerequisite.course_itemable_id);
        },

        targetParent() {
            if (!this.target) return null;

            let target;

            if (this.template && this.target.parent) {
                target = this.target.parent;
            }

            if (!this.template && !isEmpty(this.target.family)) {
                const parent = get(this.target, 'family[1]');

                if (parent) {
                    target = parent;
                }
            }

            if (!target) return null;

            return this.find('uuid', target);
        },

        template() {
            return this.$route.name.includes('template');
        },

        titleText() {
            if (this.hasPrerequisite) return '';

            if (get(this.item, 'section_type') === 'break') return '';

            if ((this.locked || this.unlocked) && !this.template) {
                return `Available at ${this.formatDate(this.item.visible_at, 'MM/DD/YY hh:mm:ss a')}`;
            }

            if ((this.locked || this.unlocked) && this.template && this.hasAllProperties(this.item.visible_at)) {
                const { week } = this.item.visible_at;
                const day = this.days[this.item.visible_at.day].name;
                const time = moment(this.item.visible_at.time).format('hh:mm a');

                return `Available on Week: ${week}, Day: ${day}, Time: ${time}`;
            }

            return '';
        },

        targetParentTitle() {
            if (!this.targetParent) return null;

            const isWeekType = get(this.targetParent, 'section_type') === 'week';

            if (isWeekType) {
                const weekNumber = get(this.targetParent, 'display_week_number');
                const sectionTitle = get(this.targetParent, 'text');
                const hasUntitledText = sectionTitle.includes('Untitled');

                return `Week ${weekNumber}${sectionTitle && !hasUntitledText ? ` (${sectionTitle})` : ''}`;
            }

            return this.targetParent.text;
        },

        prereqTitle() {
            if (!this.target) return null;

            if (this.targetParent) {
                return `${this.targetParentTitle}: ${this.target.text}`;
            }

            if (!this.target.title) {
                return `Week ${this.target.display_week_number}`;
            }

            return this.target.text;
        },
    },

    methods: {
        hasAllProperties(visibleAt) {
            return visibleAt && visibleAt.week && visibleAt.day && visibleAt.time;
        },

        find(key, target) {
            if (!this.menu) return undefined;

            let result;

            const root = Array.isArray(this.menu)
                ? this.menu
                : [
                      ...(this.menu.primary || []),
                      ...(this.menu.curriculum?.misc_pre || []),
                      ...(this.menu.curriculum?.weeks?.past || []),
                      ...(this.menu.curriculum?.weeks?.present || []),
                      ...(this.menu.curriculum?.weeks?.future || []),
                      ...(this.menu.curriculum?.misc_post || []),
                      ...(this.menu.meta || []),
                  ];

            this.search.do(root, 'items', item => {
                if (item[key] === target) {
                    result = item;
                }
            });

            return result;
        },
    },
};
